import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, formatShortDate, loadTranslations, t } from "../shared/utils"

const STORAGE_KEY = "marketplace_view"

export default class extends Controller {
  static targets = ["results", "sentinel", "form", "count", "viewTiles", "viewList"]
  static values = { apiUrl: String }

  connect() {
    loadTranslations()
    this.page = 1
    this.hasMore = false
    this.loading = false
    this.filterTimeout = null
    this.view = (typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "list"
    this._applyView()
    this._setupInfiniteScroll()
    this._setupFilters()
    this._fetchResults(1, true)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.hasFormTarget && this._boundFilter) {
      this.formTarget.removeEventListener("input", this._boundFilter)
      this.formTarget.removeEventListener("change", this._boundFilter)
      this.formTarget.removeEventListener("submit", this._boundSubmit)
    }
  }

  setViewTiles() {
    this.view = "tiles"
    this._applyView()
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "tiles")
  }

  setViewList() {
    this.view = "list"
    this._applyView()
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "list")
  }

  _applyView() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.classList.remove("marketplace-view-tiles", "marketplace-view-list")
    this.resultsTarget.classList.add(`marketplace-view-${this.view}`)
    if (this.hasViewTilesTarget) this.viewTilesTarget.classList.toggle("active", this.view === "tiles")
    if (this.hasViewListTarget) this.viewListTarget.classList.toggle("active", this.view === "list")
  }

  _setupFilters() {
    if (!this.hasFormTarget) return
    this._boundFilter = this._onFilterChange.bind(this)
    this._boundSubmit = this._onSubmit.bind(this)
    this.formTarget.addEventListener("input", this._boundFilter)
    this.formTarget.addEventListener("change", this._boundFilter)
    this.formTarget.addEventListener("submit", this._boundSubmit)
  }

  _onFilterChange(event) {
    if (event.target.name === "q") return
    const isCityInput = event.target.name === "city" && event.type === "input"
    if (isCityInput) {
      const val = (event.target.value || "").trim()
      if (val.length >= 3 || val.length === 0) {
        clearTimeout(this.filterTimeout)
        this.filterTimeout = setTimeout(() => this._fetchResults(1, true), 400)
      }
      return
    }
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this._fetchResults(1, true), 200)
  }

  _onSubmit(event) {
    event.preventDefault()
    this._fetchResults(1, true)
  }

  _setupInfiniteScroll() {
    if (!this.hasSentinelTarget) return
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && this.hasMore && !this.loading) {
        this._fetchResults(this.page + 1, false)
      }
    }, { rootMargin: "200px", threshold: 0.1 })
    this.observer.observe(this.sentinelTarget)
  }

  async _fetchResults(page, replace) {
    if (this.loading) return
    this.loading = true

    if (replace) {
      this.resultsTarget.style.opacity = "0.4"
      this.resultsTarget.style.pointerEvents = "none"
    } else if (this.hasSentinelTarget) {
      this.sentinelTarget.innerHTML = '<div class="text-center py-3"><i class="fa-solid fa-spinner fa-spin"></i></div>'
    }

    try {
      const params = this._buildParams(page)
      const json = await apiGet(this.apiUrlValue, params)
      const { data, meta } = json

      if (replace) {
        this.resultsTarget.innerHTML = data.length > 0
          ? data.map(l => this._renderCard(l)).join("")
          : this._renderEmpty()
      } else {
        data.forEach(l => this.resultsTarget.insertAdjacentHTML("beforeend", this._renderCard(l)))
      }

      this.page = meta.current_page
      this.hasMore = meta.has_more
      if (this.hasCountTarget) this.countTarget.textContent = String(meta.total_count)

      if (this.hasSentinelTarget) {
        this.sentinelTarget.innerHTML = this.hasMore
          ? '<div class="text-center py-3"><i class="fa-solid fa-spinner fa-spin"></i></div>'
          : ""
      }
    } catch (error) {
      console.error("Error fetching marketplace listings:", error)
      if (this.hasSentinelTarget) this.sentinelTarget.innerHTML = ""
    } finally {
      this.loading = false
      this.resultsTarget.style.opacity = ""
      this.resultsTarget.style.pointerEvents = ""
    }
  }

  _buildParams(page) {
    if (!this.hasFormTarget) return { page }
    const formData = new FormData(this.formTarget)
    const params = {}
    for (const [key, value] of formData.entries()) {
      if (value) params[key] = value
    }
    params.page = page
    return params
  }

  _renderCard(listing) {
    const url = `/marketplace/${listing.id}`
    const title = escapeHtml(listing.title || "")
    const truncTitle = title.length > 40 ? title.substring(0, 40) + "…" : title
    const category = escapeHtml(t(`categories.${listing.category}`))
    const condition = escapeHtml(t(`conditions.${listing.condition}`))
    const location = this._locationDisplay(listing)
    const dates = this._datesDisplay(listing)
    const price = this._priceDisplay(listing)
    const thumb = this._thumbHtml(listing, url)
    const seller = this._sellerBadge(listing)

    return `<div class="marketplace-result-item col-lg-3 col-md-4 col-sm-6">
      <div class="member__item"><div class="member__inner">
        <div class="member__thumb">${thumb}</div>
        <div class="member__content">
          <div class="member__content-body">
            <a href="${url}"><h5>${truncTitle}</h5></a>
            <p class="text-muted small mb-0">${category} &bull; ${condition}${location ? ` &bull; <i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(location)}` : ""}</p>
            ${dates ? `<p class="text-muted small mb-0 marketplace-dates-row">${escapeHtml(dates)}</p>` : ""}
          </div>
          <div class="member__content-price">${seller}<a href="${url}"><span class="marketplace-price">${price}</span></a></div>
        </div>
      </div></div>
    </div>`
  }

  _thumbHtml(listing, url) {
    if (listing.primary_media_url && listing.primary_media_type === "image") {
      return `<a href="${url}"><img src="${escapeHtml(listing.primary_media_url)}" alt="${escapeHtml(listing.title)}" class="w-100" style="object-fit: cover; height: 200px;"></a>`
    }
    const icon = listing.has_video ? "fa-video" : "fa-image"
    return `<a href="${url}"><div class="bg-light d-flex align-items-center justify-content-center" style="height: 200px;"><i class="fa-solid ${icon} fa-3x text-muted"></i></div></a>`
  }

  _sellerBadge(listing) {
    if (listing.seller_type === "company") {
      return `<span class="marketplace-seller-badge" title="${escapeHtml(t("seller_business"))}"><i class="fa-solid fa-store text-muted"></i></span>`
    }
    if (listing.seller_avatar_url) {
      return `<span class="marketplace-seller-badge" title="${escapeHtml(t("seller_private"))}"><img src="${escapeHtml(listing.seller_avatar_url)}" alt="${escapeHtml(listing.seller_name)}" class="marketplace-seller-avatar"></span>`
    }
    return `<span class="marketplace-seller-badge" title="${escapeHtml(t("seller_private"))}"><i class="fa-solid fa-user text-muted"></i></span>`
  }

  _locationDisplay(listing) {
    const parts = []
    if (listing.city) parts.push(listing.city)
    if (listing.voivodeship) {
      const label = t(`voivodeships.${listing.voivodeship}`)
      if (label !== `voivodeships.${listing.voivodeship}`) parts.push(label)
      else parts.push(listing.voivodeship)
    }
    return parts.join(", ")
  }

  _datesDisplay(listing) {
    const items = []
    if (listing.published_at) items.push(`${t("published_at")}: ${formatShortDate(listing.published_at)}`)
    if (listing.expires_at) items.push(`${t("expires_at")}: ${formatShortDate(listing.expires_at)}`)
    return items.join(" • ")
  }

  _priceDisplay(listing) {
    if (!listing.price_cents || listing.price_cents === 0) {
      return `<span class="marketplace-price-icon" title="${escapeHtml(t("free_giveaway"))}"><i class="fa-solid fa-gift"></i></span>`
    }
    return escapeHtml(listing.price_formatted)
  }

  _renderEmpty() {
    return `<div class="marketplace-search__empty col-12">
      <div class="text-center py-5">
        <i class="fa-solid fa-search fa-3x text-muted mb-3"></i>
        <h4>${escapeHtml(t("no_results"))}</h4>
      </div>
    </div>`
  }
}
